import { Link } from 'react-router';
import { eventTypeLabel, formatDistance, formatEventWhen } from '../../events/format';
import type { EventSummary } from '../../events/types';
import { Badge, Heading, Stack, Text } from '../../ui';

/** Props for {@link EventCard}. */
export interface EventCardProps {
  readonly event: EventSummary;
}

/**
 * One event as a card in a list, linking to its detail page.
 *
 * @param props - Component props.
 * @returns The card element.
 */
export function EventCard({ event }: EventCardProps): React.JSX.Element {
  const distance = formatDistance(event.distanceKm);

  return (
    <Link to={`/events/${event.id}`} className="gm-event-card">
      <Stack direction="row" gap="sm" align="center" justify="space-between" wrap>
        <Heading level={2} size="md">
          {event.title}
        </Heading>
        <Stack direction="row" gap="xs" wrap>
          {event.isStale && <Badge tone="warning">Stale</Badge>}
          {event.callerRsvp != null && <Badge tone="accent">{event.callerRsvp}</Badge>}
        </Stack>
      </Stack>

      <Text size="sm" muted>
        {formatEventWhen(event)} · {eventTypeLabel(event.type)}
      </Text>
      <Text size="sm" muted>
        {event.address}
        {distance === '' ? '' : ` · ${distance} away`}
      </Text>

      <Stack direction="row" gap="sm" wrap>
        <Badge tone="neutral">{event.goingCount} going</Badge>
        {event.interestedCount > 0 && (
          <Badge tone="neutral">{event.interestedCount} interested</Badge>
        )}
      </Stack>
    </Link>
  );
}
